import DriveItem from './DriveItem';
import { useState, useEffect } from 'react';
import { Select } from '@mantine/core';
import { getDrives } from '../../apis/agent';
import { useLocation } from 'react-router-dom';

export default function DriveList() {
  const location = useLocation();
  const { device } = location.state || {};
  const uuid = device?.uuid || '';
  const sys_uuid = device?.sys_uuid || '';
  const device_id = device?.id || '';
  const licence_id=device?.licence_id||''
  //console.log(licence_id)
  const allowedDisks = device?.disks_info|| [];
  const licenceType  =device?.licenceType ||""
  const deletions=device?.deletionAllowed
  const deletionUsed=device?.deletionUsed
  //console.log(device)

  const [sortOption, setSortOption] = useState('Sort by Size');
  const [drives, setDrives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
// Dialog state
  const [showDialog, setShowDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const sortOptions = [
    'Sort by Size',
    'Sort by Status High',
    'Sort by Status Low',
  ];

  useEffect(() => {
    const fetchDrives = async () => {
      if (!uuid) {
        setError('Device UUID is missing.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await getDrives(uuid, sys_uuid);
        if (response && Array.isArray(response.data)) {
          const mappedDrives = response.data.map(drive => {
            const totalGB = Math.round(drive.total_space / (1024 * 1024 * 1024));
            const freeGB = Math.round(drive.free_space / (1024 * 1024 * 1024));
            const usedGB = totalGB - freeGB;
            const percentage = Math.round((usedGB / totalGB) * 100);

            let status;
            if (percentage < 50) {
              status = 'Healthy';
            } else if (percentage < 80) {
              status = 'Warning';
            } else {
              status = 'Critical';
            }
             // Determine if drive is allowed based on licenceType and allowedDisks
             const driveLetter = `${drive.drive_letter}/`;
            let isAllowed = true;
            if (licenceType !== 'single_device') {
              // Only drives in allowedDisks can be clicked
              isAllowed = allowedDisks.includes(drive.serial_number) 
            } else {
              // For single_device, disable all
              isAllowed = false;
            }
            return {
              name: driveLetter,
              label: `${totalGB}GB ${drive.drive_type}`,
              used: usedGB,
              total: totalGB,
              status: status,
              uuid: uuid,
              isAllowed:isAllowed,
            };
          });
          setDrives(mappedDrives);
        } else {
          console.error('Invalid response format:', response);
          setDrives([]);
          setError('Failed to load drives: Invalid response format');
        }
      } catch (err) {
        console.error('Error fetching drives:', err);
        setDrives([]);
        if (err.message === 'UUID is required to fetch drives') {
          setError('Failed to load drives: Device UUID is required.');
        } else if (err.response) {
          setError(`Failed to load drives: Server error (${err.response.status})`);
        } else if (err.request) {
          setError('Failed to load drives: Unable to connect to the server. This may be due to a CORS policy issue or the agent being offline.');
        } else {
          setError(`Failed to load drives: ${err.message}`);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDrives();
  }, [uuid]);

  const getSortedDrives = () => {
    const statusOrder = {
      Healthy: 1,
      Warning: 2,
      Critical: 3,
    };

    return [...drives].sort((a, b) => {
      if (sortOption === 'Sort by Size') {
        return a.total - b.total;
      } else if (sortOption === 'Sort by Status Low') {
        return statusOrder[a.status] - statusOrder[b.status];
      } else if (sortOption === 'Sort by Status High') {
        return statusOrder[b.status] - statusOrder[a.status];
      }
      return 0;
    });
  };

  const sortedDrives = getSortedDrives();

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-md p-4 md:p-6 w-full">
        <h3
          style={{
            fontFamily: 'Nunito Sans, sans-serif',
            fontSize: '24px',
            fontWeight: 700,
            color: '#202224',
            marginBottom: '16px',
          }}
        >
          Available Drives
        </h3>
        <div
          style={{
            fontFamily: 'Nunito Sans, sans-serif',
            fontSize: '16px',
            color: '#595C60',
            textAlign: 'center',
          }}
        >
          Loading drives...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-md p-4 md:p-6 w-full">
        <h3
          style={{
            fontFamily: 'Nunito Sans, sans-serif',
            fontSize: '24px',
            fontWeight: 700,
            color: '#202224',
            marginBottom: '16px',
          }}
        >
          Available Drives
        </h3>
        <div
          style={{
            fontFamily: 'Nunito Sans, sans-serif',
            fontSize: '16px',
            color: '#E74C3C',
            textAlign: 'center',
          }}
        >
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-md p-4 md:p-6 w-full">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
        <h3
          style={{
            fontFamily: 'Nunito Sans, sans-serif',
            fontSize: '24px',
            fontWeight: 700,
            color: '#202224',
          }}
        >
          Available Drives
        </h3>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <Select
            value={sortOption}
            onChange={(value) => setSortOption(value)}
            data={sortOptions}
            placeholder="Sort Options"
            styles={{
              input: {
                fontFamily: 'Nunito Sans, sans-serif',
                fontSize: { xs: '10px', sm: '12px' },
                fontWeight: 500,
                color: '#202224',
                border: '0.6px solid #202224',
                backgroundColor: '#FCFDFD',
                borderRadius: '6px',
                height: '36px',
                width: '150px',
                '&:focus': {
                  borderColor: '#FF6B00',
                  outline: 'none',
                },
              },
              item: {
                fontFamily: 'Nunito Sans, sans-serif',
                fontSize: { xs: '10px', sm: "12px" },
                color: '#202224',
                '&[data-selected]': {
                  backgroundColor: '#FF6B00',
                  color: '#FFFFFF',
                  '&:hover': {
                    backgroundColor: '#FF6B00',
                    color: '#FFFFFF',
                  },
                },
                '&:hover': {
                  backgroundColor: '#FFF3E5',
                },
              },
            }}
          />
        </div>
      </div>
      {drives.length === 0 ? (
        <div
          style={{
            fontFamily: 'Nunito Sans, sans-serif',
            fontSize: '16px',
            color: '#595C60',
            textAlign: 'center',
          }}
        >
          No drives available.
        </div>
      ) : (
        <div className="space-y-4">
          {sortedDrives.map((drive, index) => (
            <DriveItem key={index} {...drive} sys_uuid={sys_uuid} device_id={device_id} licence_id={licence_id}  
            disabled={!drive.isAllowed} />
          ))}
        </div>
      )}
    </div>
  );
}